import TextField from "@mui/material/TextField";
import { Controller, useFormContext } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { AdSuburbViewModel } from "@/sell/models/AdSuburb";
import { CountryCode } from "@/constants/CountryOptions";
import { useGooglePlaces } from "@/app/layout/useGooglePlaces";

function PlacesAutocomplete({
  label,
  countryCode,
  onChange,
  inputRef,
  error,
  helperText,
  initialValue,
}: {
  label: string;
  countryCode?: CountryCode;
  onChange: (value: AdSuburbViewModel) => void;
  inputRef: React.Ref<HTMLInputElement>;
  error?: boolean;
  helperText?: string;
  initialValue: AdSuburbViewModel;
}) {
  const { setInput, options, getPlaceDetails } = useGooglePlaces({
    countryCode,
  });

  return (
    <Autocomplete
      value={initialValue}
      options={options}
      onChange={async (_, value) => {
        onChange(await getPlaceDetails(value));
      }}
      noOptionsText="Search by name and select from the list"
      getOptionLabel={(option) => option.description}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          onChange={(evt) => setInput(evt.target.value)}
          inputRef={inputRef}
          error={error}
          helperText={helperText}
          autoComplete="new-password"
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              autoComplete: "new-password", // disable autocomplete and autofill
            },
          }}
        />
      )}
    />
  );
}

export default function ControlledSuburbField({
  name,
  label,
  countryName,
}: {
  name: string;
  label: string;
  countryName: string;
}) {
  const { control, watch } = useFormContext();
  const countryCode = watch(countryName) as CountryCode | undefined;

  return (
    <Controller
      key={countryCode}
      name={name}
      control={control}
      render={({ field: { ref, ...fieldRest }, fieldState }) => (
        <PlacesAutocomplete
          label={label}
          inputRef={ref}
          {...fieldRest}
          initialValue={fieldRest.value}
          countryCode={countryCode}
          onChange={(value) => {
            fieldRest.onChange(value);
          }}
          error={!!fieldState.error}
          helperText={fieldState.error ? fieldState.error.message : undefined}
        />
      )}
    />
  );
}
